import React, { useState, useEffect } from 'react';
import { UserCheck, Clock, Search, Filter, ShieldCheck, XCircle } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import styles from './Visitors.module.css';

const getImageUrl = (path) => {
  if (!path) return '';
  const baseUrl = import.meta.env.VITE_API_URL.replace(/\/api\/?$/, '');
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

const Visitors = () => {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchVisitors();
  }, []);

  const fetchVisitors = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/visitors');
      setVisitors(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/visitors/${id}/status`, { status });
      setVisitors(visitors.map(v => v._id === id ? data : v));
      toast.success('Status updated successfully!');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Visitor Logs" 
        breadcrumb={['Society Management', 'Visitors']} 
      />

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.searchBox}>
            <Search size={16} className={styles.searchIcon} />
            <input type="text" placeholder="Search visitors or flat no..." />
          </div>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Visitor Name</th>
                <th>Visiting Flat</th>
                <th>Purpose</th>
                <th>Time/Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="6">Loading...</td></tr> : visitors.map(v => (
                <tr key={v._id}>
                  <td>
                    <div className={styles.visitorName}>
                      <div className={styles.avatarSmall}>
                        {v.photo ? <img src={getImageUrl(v.photo)} onClick={(e) => { e.stopPropagation(); setSelectedImage(v.photo); }} alt="Visitor" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer'}} /> : v.name.charAt(0)}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <strong>{v.name}</strong>
                        {v.phone && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>{v.phone}</span>}
                      </div>
                    </div>
                  </td>
                  <td><strong>{v.resident ? `${v.resident.wing || ''}-${v.resident.flatNumber || ''}` : (v.flatNumber || '-')}</strong></td>
                  <td><span className={styles.badgePurpose}>{v.purpose}</span></td>
                  <td><span className={styles.time}><Clock size={12} /> {v.expectedDate ? new Date(v.expectedDate).toLocaleDateString() + ' ' + (v.expectedTime || '') : new Date(v.createdAt).toLocaleDateString()}</span></td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[v.status.toLowerCase()]}`}>
                      {(v.status === 'Expected' || v.status === 'Pending') && <Clock size={12} />}
                      {v.status === 'Allowed' && <UserCheck size={12} />}
                      {v.status}
                    </span>
                  </td>
                  <td>
                    {(v.status === 'Expected' || v.status === 'Pending') && (
                      <div className={styles.actionBtns}>
                        <button className={styles.btnApprove} onClick={() => handleAction(v._id, 'Allowed')}>
                          <ShieldCheck size={16} /> Allow
                        </button>
                        <button className={styles.btnDeny} onClick={() => handleAction(v._id, 'Denied')}>
                          <XCircle size={16} /> Deny
                        </button>
                      </div>
                    )}
                    {v.status === 'Allowed' && (
                      <button className={styles.btnExit} onClick={() => handleAction(v._id, 'Completed')}>
                        Mark Exit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.mobileList}>
            {loading ? <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div> : visitors.map(v => (
              <div key={v._id} className={styles.mobileCard}>
                <div className={styles.cardTop}>
                  <div className={styles.visitorName}>
                    <div className={styles.avatarSmall}>
                      {v.photo ? <img src={getImageUrl(v.photo)} onClick={(e) => { e.stopPropagation(); setSelectedImage(v.photo); }} alt="Visitor" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer'}} /> : v.name.charAt(0)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'flex-start' }}>
                      <strong>{v.name}</strong>
                      {v.phone && <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 'normal' }}>{v.phone}</span>}
                      <span className={styles.badgePurpose}>{v.purpose}</span>
                    </div>
                  </div>
                  <span className={`${styles.badgeStatus} ${styles[v.status.toLowerCase()]}`}>
                    {(v.status === 'Expected' || v.status === 'Pending') && <Clock size={12} />}
                    {v.status === 'Allowed' && <UserCheck size={12} />}
                    {v.status}
                  </span>
                </div>
                <div className={styles.cardMid}>
                  <p><strong>Flat:</strong> {v.resident ? `${v.resident.wing || ''}-${v.resident.flatNumber || ''}` : (v.flatNumber || '-')}</p>
                  <span className={styles.time}><Clock size={12} /> {v.expectedDate ? new Date(v.expectedDate).toLocaleDateString() + ' ' + (v.expectedTime || '') : new Date(v.createdAt).toLocaleDateString()}</span>
                </div>
                <div className={styles.cardBottom}>
                  {(v.status === 'Expected' || v.status === 'Pending') && (
                    <div className={styles.actionBtns}>
                      <button className={styles.btnApprove} onClick={() => handleAction(v._id, 'Allowed')}>
                        <ShieldCheck size={16} /> Allow
                      </button>
                      <button className={styles.btnDeny} onClick={() => handleAction(v._id, 'Denied')}>
                        <XCircle size={16} /> Deny
                      </button>
                    </div>
                  )}
                  {v.status === 'Allowed' && (
                    <button className={styles.btnExit} onClick={() => handleAction(v._id, 'Completed')}>
                      Mark Exit
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedImage && (
        <div 
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
          onClick={() => setSelectedImage(null)}
        >
          <img src={getImageUrl(selectedImage)} style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '8px' }} alt="Enlarged Visitor" />
          <button 
            onClick={() => setSelectedImage(null)}
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', color: 'white', border: 'none', fontSize: '40px', cursor: 'pointer' }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default Visitors;
